import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { googleCalendarUrl, nextStatus, periodicityLabels, priorityLabels, sourceLabels, statusLabels } from '../../domain/taskRules'
import { formatDocument } from '../../domain/document'
import { errorMessage } from '../../lib/errors'
import { formatBusinessDate, formatDueDateTime } from '../../lib/time'
import { TaskHistory } from './TaskHistory'
import { useSetTaskArchived, useSetTaskStatus, useTask } from './task.queries'

export function TaskDetailPage() {
  const { id = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const task = useTask(id)
  const statusMutation = useSetTaskStatus(id)
  const archiveMutation = useSetTaskArchived(id)
  const successMessage = (location.state as { message?: string } | null)?.message

  if (task.isLoading) return <p>Carregando tarefa…</p>
  if (task.isError) return <p className="notice-error">{errorMessage(task.error)}</p>
  if (!task.data) return <p className="notice-error">Tarefa não encontrada.</p>

  const item = task.data
  const statusAction = (command: 'start' | 'complete' | 'reopen') => {
    statusMutation.mutate(nextStatus(item.status, command))
  }
  const archiveAction = () => {
    const isArchiving = !item.archived_at
    archiveMutation.mutate(isArchiving, {
      onSuccess: () => {
        if (isArchiving) navigate('/', { replace: true })
      },
    })
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <Link className="font-bold text-blue-700 hover:underline" to={item.archived_at ? '/?arquivadas=1' : '/'}>← Voltar ao painel</Link>
        <div className="flex flex-wrap gap-2">
          <a className="btn-secondary text-center" href={googleCalendarUrl(item)} target="_blank" rel="noreferrer">Abrir no Google Agenda</a>
          <Link className="btn-secondary text-center" to={`/tarefas/${item.id}/editar`}>Editar tarefa</Link>
        </div>
      </div>
      {successMessage ? <p className="notice-success mb-4" role="status">{successMessage}</p> : null}
      {(statusMutation.isError || archiveMutation.isError) ? (
        <p className="notice-error mb-4" role="alert">{errorMessage(statusMutation.error ?? archiveMutation.error)}</p>
      ) : null}

      <article className="card overflow-hidden">
        <header className="border-b border-slate-200 bg-slate-950 p-5 text-white sm:p-7">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-bold uppercase tracking-wider text-blue-300">{item.client_name}</p>
              <h1 className={`mt-1 text-3xl font-extrabold ${item.archived_at || item.status === 'completed' ? 'text-white' : item.is_overdue ? 'text-red-300' : 'text-blue-300'}`}>{item.title}</h1>
            </div>
            <span className="rounded-full bg-white/15 px-3 py-1 text-sm font-extrabold">{statusLabels[item.status]}</span>
          </div>
          {item.archived_at ? <p className="mt-4 rounded-lg bg-amber-300/15 p-3 font-bold text-amber-100">Esta tarefa está arquivada.</p> : null}
        </header>
        <div className="grid gap-6 p-5 sm:p-7">
          {item.description ? <p className="whitespace-pre-wrap leading-7 text-slate-700">{item.description}</p> : <p className="italic text-slate-500">Sem descrição.</p>}
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div><dt className="text-xs font-bold uppercase tracking-wide text-slate-500">Cliente</dt><dd className="mt-1 font-semibold">{item.client_name}{item.client_document ? <small className="block font-normal text-slate-500">{formatDocument(item.client_document)}</small> : null}</dd></div>
            <div><dt className="text-xs font-bold uppercase tracking-wide text-slate-500">Responsável</dt><dd className="mt-1 font-semibold">{item.assignee_name}</dd></div>
            <div><dt className="text-xs font-bold uppercase tracking-wide text-slate-500">Departamento</dt><dd className="mt-1 font-semibold">{item.department_name}</dd></div>
            <div><dt className="text-xs font-bold uppercase tracking-wide text-slate-500">Origem</dt><dd className="mt-1 font-semibold">{sourceLabels[item.source]}</dd></div>
            <div><dt className="text-xs font-bold uppercase tracking-wide text-slate-500">Prioridade</dt><dd className="mt-1 font-semibold">{priorityLabels[item.priority]}</dd></div>
            <div><dt className="text-xs font-bold uppercase tracking-wide text-slate-500">Periodicidade</dt><dd className="mt-1 font-semibold">{periodicityLabels[item.periodicity]}</dd></div>
            <div><dt className="text-xs font-bold uppercase tracking-wide text-slate-500">Recebida em</dt><dd className="mt-1 font-semibold">{formatBusinessDate(item.received_date)} às {item.received_time.slice(0, 5)}</dd></div>
            <div><dt className="text-xs font-bold uppercase tracking-wide text-slate-500">Prazo</dt><dd className={`mt-1 font-semibold ${item.is_overdue ? 'text-red-700' : item.is_due_soon ? 'text-amber-700' : ''}`}>{formatDueDateTime(item.due_date, item.due_time)}</dd></div>
            <div><dt className="text-xs font-bold uppercase tracking-wide text-slate-500">Situação do prazo</dt><dd className="mt-1 font-semibold">{item.is_overdue ? 'Atrasada' : item.is_due_soon ? 'Prazo próximo' : 'No prazo'}</dd></div>
          </dl>
          <div className="flex flex-wrap gap-3 border-t border-slate-200 pt-5">
            {item.status === 'not_started' ? <button className="btn-primary" disabled={statusMutation.isPending} onClick={() => statusAction('start')} type="button">Iniciar</button> : null}
            {item.status !== 'completed' ? <button className="btn-primary" disabled={statusMutation.isPending} onClick={() => statusAction('complete')} type="button">Concluir</button> : null}
            {item.status === 'completed' ? <button className="btn-primary" disabled={statusMutation.isPending} onClick={() => statusAction('reopen')} type="button">Reabrir em andamento</button> : null}
            <button className={item.archived_at ? 'btn-secondary' : 'btn-danger'} disabled={archiveMutation.isPending} onClick={archiveAction} type="button">
              {item.archived_at ? 'Recuperar tarefa' : 'Arquivar tarefa'}
            </button>
          </div>
        </div>
      </article>
      <div className="mt-6"><TaskHistory taskId={item.id} /></div>
    </div>
  )
}
